import React, { useState, useEffect } from 'react';
import {
  architectureAgentService,
  ValidationResult,
  CodeQualityAnalysis,
  ArchitectureGuidanceResponse,
  BestPracticesResponse,
  AvailableOptions,
} from '../../services/api/architectureAgentService';
import './ArchitectureAgent.css';

interface ArchitectureAgentProps {
  className?: string;
}

export const ArchitectureAgent: React.FC<ArchitectureAgentProps> = ({ className = '' }) => {
  // State for different sections
  const [activeTab, setActiveTab] = useState<
    'validation' | 'guidance' | 'best-practices' | 'analysis'
  >('validation');

  // State for code validation
  const [code, setCode] = useState('');
  const [componentType, setComponentType] = useState<
    'service' | 'endpoint' | 'model' | 'frontend_service' | 'frontend_component'
  >('service');
  const [componentName, setComponentName] = useState('');
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // State for code quality analysis
  const [qualityAnalysis, setQualityAnalysis] = useState<CodeQualityAnalysis | null>(null);
  const [fileType] = useState('python');

  // State for architecture guidance
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [guidance, setGuidance] = useState<ArchitectureGuidanceResponse | null>(null);
  const [isLoadingGuidance, setIsLoadingGuidance] = useState(false);

  // State for best practices
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [bestPractices, setBestPractices] = useState<BestPracticesResponse | null>(null);
  const [isLoadingPractices, setIsLoadingPractices] = useState(false);

  // State for available options
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  // Load available options on component mount
  useEffect(() => {
    void loadAvailableOptions();
  }, []);

  const loadAvailableOptions = async () => {
    try {
      setIsLoadingOptions(true);
      const options = await architectureAgentService.getAllAvailableOptions();
      setAvailableOptions(options);
    } catch (error) {
      console.error('Failed to load available options:', error);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const handleValidateCode = async () => {
    if (!code.trim() || !componentName.trim()) {
      alert('Please provide both code and component name');
      return;
    }

    try {
      setIsValidating(true);
      const results = await architectureAgentService.validateCode({
        code,
        component_type: componentType,
        component_name: componentName,
      });
      setValidationResults(results);
    } catch (error) {
      console.error('Validation failed:', error);
      alert('Code validation failed. Please check the console for details.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleAnalyzeQuality = async () => {
    if (!code.trim()) {
      alert('Please provide code to analyze');
      return;
    }

    try {
      setIsValidating(true);
      const analysis = await architectureAgentService.analyzeCodeQuality({
        code,
        file_type: fileType,
      });
      setQualityAnalysis(analysis);
    } catch (error) {
      console.error('Quality analysis failed:', error);
      alert('Code quality analysis failed. Please check the console for details.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleGetGuidance = async () => {
    if (!selectedScenario) {
      alert('Please select a scenario');
      return;
    }

    try {
      setIsLoadingGuidance(true);
      const guidanceData = await architectureAgentService.getArchitectureGuidance({
        scenario: selectedScenario as
          | 'new_service'
          | 'new_endpoint'
          | 'new_model'
          | 'prime_artifact_creation'
          | 'artifact_instance_creation',
      });
      setGuidance(guidanceData);
    } catch (error) {
      console.error('Failed to get guidance:', error);
      alert('Failed to get architecture guidance. Please check the console for details.');
    } finally {
      setIsLoadingGuidance(false);
    }
  };

  const handleGetBestPractices = async () => {
    if (!selectedArea) {
      alert('Please select an area');
      return;
    }

    try {
      setIsLoadingPractices(true);
      const practices = await architectureAgentService.getBestPractices({
        area: selectedArea as
          | 'session_management'
          | 'api_design'
          | 'model_design'
          | 'prime_model'
          | 'artifact_instance'
          | 'relationship_management',
      });
      setBestPractices(practices);
    } catch (error) {
      console.error('Failed to get best practices:', error);
      alert('Failed to get best practices. Please check the console for details.');
    } finally {
      setIsLoadingPractices(false);
    }
  };

  const getValidationSummary = () => {
    return architectureAgentService.getValidationSummary(validationResults);
  };

  const formatValidationResults = () => {
    return architectureAgentService.formatValidationResults(validationResults);
  };

  const renderValidationTab = () => (
    <div className="architecture-agent-tab">
      <h3>Code Validation</h3>
      <div className="validation-form">
        <div className="form-group">
          <label>Component Type:</label>
          <select
            value={componentType}
            onChange={(e) =>
              setComponentType(
                e.target.value as
                  | 'service'
                  | 'endpoint'
                  | 'model'
                  | 'frontend_service'
                  | 'frontend_component'
              )
            }
            disabled={isLoadingOptions}
          >
            {availableOptions.component_types?.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Component Name:</label>
          <input
            type="text"
            value={componentName}
            onChange={(e) => setComponentName(e.target.value)}
            placeholder="Enter component name"
          />
        </div>

        <div className="form-group">
          <label>Code:</label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste your code here..."
            rows={10}
          />
        </div>

        <div className="form-actions">
          <button
            onClick={handleValidateCode}
            disabled={isValidating || !code.trim() || !componentName.trim()}
            className="btn btn-primary"
          >
            {isValidating ? 'Validating...' : 'Validate Code'}
          </button>
          <button
            onClick={handleAnalyzeQuality}
            disabled={isValidating || !code.trim()}
            className="btn btn-secondary"
          >
            {isValidating ? 'Analyzing...' : 'Analyze Quality'}
          </button>
        </div>
      </div>

      {validationResults.length > 0 && (
        <div className="validation-results">
          <h4>Validation Results</h4>
          {(() => {
            const summary = getValidationSummary();
            const formatted = formatValidationResults();

            return (
              <div className="validation-summary">
                <div className={`summary-badge ${summary.hasIssues ? 'has-issues' : 'no-issues'}`}>
                  {summary.total} total issues ({summary.errors} errors, {summary.warnings}{' '}
                  warnings, {summary.info} info)
                </div>

                {formatted.errors.length > 0 && (
                  <div className="validation-section errors">
                    <h5>Errors ({formatted.errors.length})</h5>
                    {formatted.errors.map((result, index) => (
                      <div key={index} className="validation-item error">
                        <div className="validation-message">{result.message}</div>
                        {result.recommendation && (
                          <div className="validation-recommendation">
                            <strong>Recommendation:</strong> {result.recommendation}
                          </div>
                        )}
                        {result.code_snippet && (
                          <div className="validation-code">
                            <strong>Code:</strong> <code>{result.code_snippet}</code>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {formatted.warnings.length > 0 && (
                  <div className="validation-section warnings">
                    <h5>Warnings ({formatted.warnings.length})</h5>
                    {formatted.warnings.map((result, index) => (
                      <div key={index} className="validation-item warning">
                        <div className="validation-message">{result.message}</div>
                        {result.recommendation && (
                          <div className="validation-recommendation">
                            <strong>Recommendation:</strong> {result.recommendation}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {formatted.info.length > 0 && (
                  <div className="validation-section info">
                    <h5>Info ({formatted.info.length})</h5>
                    {formatted.info.map((result, index) => (
                      <div key={index} className="validation-item info">
                        <div className="validation-message">{result.message}</div>
                        {result.recommendation && (
                          <div className="validation-recommendation">
                            <strong>Recommendation:</strong> {result.recommendation}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {qualityAnalysis && (
        <div className="quality-analysis">
          <h4>Code Quality Analysis</h4>
          <div className="quality-metrics">
            <div className="metric">
              <span className="metric-label">Async Usage:</span>
              <span className="metric-value">{qualityAnalysis.async_usage}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Error Handling:</span>
              <span className="metric-value">{qualityAnalysis.error_handling}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Type Hints:</span>
              <span className="metric-value">{qualityAnalysis.type_hints}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Documentation:</span>
              <span className="metric-value">{qualityAnalysis.documentation}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Validation:</span>
              <span className="metric-value">{qualityAnalysis.validation}</span>
            </div>
          </div>

          {qualityAnalysis.recommendations.length > 0 && (
            <div className="quality-recommendations">
              <h5>Recommendations:</h5>
              <ul>
                {qualityAnalysis.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderGuidanceTab = () => (
    <div className="architecture-agent-tab">
      <h3>Architecture Guidance</h3>
      <div className="guidance-form">
        <div className="form-group">
          <label>Scenario:</label>
          <select
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            disabled={isLoadingOptions}
          >
            <option value="">Select a scenario...</option>
            {availableOptions.available_scenarios?.map((scenario) => (
              <option key={scenario} value={scenario}>
                {scenario.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleGetGuidance}
          disabled={isLoadingGuidance || !selectedScenario}
          className="btn btn-primary"
        >
          {isLoadingGuidance ? 'Loading...' : 'Get Guidance'}
        </button>
      </div>

      {guidance && (
        <div className="guidance-results">
          <h4>Architecture Guidance: {guidance.scenario.replace(/_/g, ' ')}</h4>

          <div className="guidance-section">
            <h5>Patterns</h5>
            <ul>
              {guidance.patterns.map((pattern, index) => (
                <li key={index}>{pattern}</li>
              ))}
            </ul>
          </div>

          <div className="guidance-section">
            <h5>Recommendations</h5>
            <ul>
              {guidance.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>

          <div className="guidance-section">
            <h5>Examples</h5>
            <ul>
              {guidance.examples.map((example, index) => (
                <li key={index}>{example}</li>
              ))}
            </ul>
          </div>

          <div className="guidance-section">
            <h5>Validation Rules</h5>
            <ul>
              {guidance.validation_rules.map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderBestPracticesTab = () => (
    <div className="architecture-agent-tab">
      <h3>Best Practices</h3>
      <div className="practices-form">
        <div className="form-group">
          <label>Area:</label>
          <select
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
            disabled={isLoadingOptions}
          >
            <option value="">Select an area...</option>
            {availableOptions.available_areas?.map((area) => (
              <option key={area} value={area}>
                {area.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleGetBestPractices}
          disabled={isLoadingPractices || !selectedArea}
          className="btn btn-primary"
        >
          {isLoadingPractices ? 'Loading...' : 'Get Best Practices'}
        </button>
      </div>

      {bestPractices && (
        <div className="practices-results">
          <h4>Best Practices: {bestPractices.area.replace(/_/g, ' ')}</h4>
          <ul>
            {bestPractices.practices.map((practice, index) => (
              <li key={index}>{practice}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className={`architecture-agent ${className}`}>
      <div className="architecture-agent-header">
        <h2>Krysalis Business Architecture Agent</h2>
        <p>Validate code, get architectural guidance, and access best practices</p>
      </div>

      <div className="architecture-agent-tabs">
        <button
          className={`tab-button ${activeTab === 'validation' ? 'active' : ''}`}
          onClick={() => setActiveTab('validation')}
        >
          Code Validation
        </button>
        <button
          className={`tab-button ${activeTab === 'guidance' ? 'active' : ''}`}
          onClick={() => setActiveTab('guidance')}
        >
          Architecture Guidance
        </button>
        <button
          className={`tab-button ${activeTab === 'best-practices' ? 'active' : ''}`}
          onClick={() => setActiveTab('best-practices')}
        >
          Best Practices
        </button>
      </div>

      <div className="architecture-agent-content">
        {isLoadingOptions && <div className="loading-message">Loading available options...</div>}

        {!isLoadingOptions && (
          <>
            {activeTab === 'validation' && renderValidationTab()}
            {activeTab === 'guidance' && renderGuidanceTab()}
            {activeTab === 'best-practices' && renderBestPracticesTab()}
          </>
        )}
      </div>
    </div>
  );
};
